// src/components/programs/ShowProgram/InvoiceOverview.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Typography, Box, Grid, Divider, CircularProgress, Chip, Stack, Alert } from '@mui/material';
import { formatCurrency } from '../ProgramForm/helpers/quotationCalculations';
import useGetProgramInvoice from '../../../hooks/invoices/useGetProgramInvoice';

const InvoiceOverview = ({ program }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const isArabic = lang === 'ar';

    const hasInvoice = !!program?.invoice?.id;

    const { data, isLoading, error } = useGetProgramInvoice(program?.id, {
        enabled: hasInvoice
    });

    const invoiceData = data?.data || {};

    if (!hasInvoice) return null;

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={30} />
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography color="error">
                    {isArabic ? "حدث خطأ أثناء تحميل الفاتورة" : "Error loading invoice details"}
                </Typography>
            </Paper>
        );
    }

    const labelHeadingStyle = {
        fontWeight: 700,
        color: '#ff9800',
        mb: 0.5,
        fontSize: '15px',
        textAlign: isArabic ? 'right' : 'left',
    };

    const detailValueStyle = {
        fontWeight: 600,
        color: '#424242',
        fontSize: '16px',
        lineHeight: 1.4,
    };

    const effectiveCurrency = invoiceData.effective_currency || invoiceData.invoice_currency || {};
    const currencySymbol = effectiveCurrency.symbol || effectiveCurrency.code || '';
    const currencyNameObj = effectiveCurrency.name;
    const currencyName = currencyNameObj?.[lang] || currencyNameObj?.en || effectiveCurrency.code || '';

    const amounts = invoiceData.display_amounts || {
        subtotal: invoiceData.subtotal,
        discount_amount: invoiceData.discount_amount,
        price_before_tax: invoiceData.price_before_tax,
        tax_amount: invoiceData.tax_amount,
        total_due: invoiceData.total_due,
    };

    const getLocalizedValue = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val.trim() || null;
        if (typeof val === 'object') {
            return val[lang] || val.ar || val.en || Object.values(val).find((v) => typeof v === 'string' && v.trim()) || null;
        }
        return String(val);
    };

    const customerName =
        getLocalizedValue(invoiceData.billed_to_customer) ||
        (invoiceData.invoice_type === 'customer' ? getLocalizedValue(invoiceData.billed_to_name) : null) ||
        getLocalizedValue(program?.description) ||
        '-';

    const agentName =
        invoiceData.invoice_type === 'customer'
            ? '-'
            : (getLocalizedValue(invoiceData.billed_to_name) ||
               getLocalizedValue(program?.agent?.name) ||
               getLocalizedValue(program?.agent_name) ||
               '-');

    return (
        <Paper
            className="invoice-overview-section"
            sx={{
                p: 3,
                mb: 3,
                mt: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '8px',
                    backgroundColor: '#4CAF50',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                },
                '@media print': {
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    marginBottom: '16px',
                    padding: '12px',
                    boxShadow: 'none',
                    border: '1px solid #ddd',
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                    {isArabic ? "ملخص الفاتورة" : "Invoice Overview"}
                </Typography>
                
                <Stack direction="row" spacing={1}>
                    <Chip 
                        label={invoiceData.invoice_type === 'agent' ? (isArabic ? 'وكيل' : 'Agent') : (isArabic ? 'عميل' : 'Customer')}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                    {invoiceData.state && (
                        <Chip 
                            label={invoiceData.state === 'settled' ? (isArabic ? 'مكتملة' : 'Settled') : (invoiceData.state === 'active' ? (isArabic ? 'نشطة' : 'Active') : invoiceData.state)}
                            size="small"
                            color={invoiceData.state === 'settled' ? 'success' : 'info'}
                        />
                    )}
                </Stack>
            </Box>
            
            <Divider sx={{ mb: 3 }} />

            {invoiceData.bank_currency_differs && (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="warning">
                        {isArabic
                            ? "تنبيه: عملة حساب البنك تختلف عن عملة العرض المحددة للفاتورة."
                            : "Warning: The bank account's currency differs from the selected display currency."
                        }
                    </Alert>
                </Box>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "رقم الفاتورة" : "Invoice Number"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {invoiceData.invoice_number}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "اسم العميل" : "Customer Name"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {customerName}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "اسم الأيجينت" : "Agent Name"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {agentName}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "تاريخ الاستحقاق" : "Payment Due By"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {invoiceData.payment_due_by || '-'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "البنك" : "Bank"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {invoiceData.bank ? (invoiceData.bank.bank_name?.[lang] || invoiceData.bank.bank_name?.en || invoiceData.bank.account_number) : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={labelHeadingStyle}>
                                {isArabic ? "عملة الفاتورة" : "Invoice Currency"}
                            </Typography>
                            <Typography variant="body1" sx={detailValueStyle}>
                                {currencyName} {currencySymbol ? `(${currencySymbol})` : ''}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8f9fa', 
                        p: 2, 
                        borderRadius: 2,
                        height: '100%'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
                            {isArabic ? "الإجماليات" : "Totals"}
                        </Typography>

                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? "الإجمالي الفرعي" : "Subtotal"}
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {currencySymbol} {formatCurrency(amounts.subtotal)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? "الخصم" : "Discount"}
                                </Typography>
                                <Typography variant="body2" fontWeight="600" color="error">
                                    - {currencySymbol} {formatCurrency(amounts.discount_amount)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? "قبل الضريبة" : "Before Tax"}
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {currencySymbol} {formatCurrency(amounts.price_before_tax)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? `الضريبة (${invoiceData.tax_percentage}%)` : `Tax (${invoiceData.tax_percentage}%)`}
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    + {currencySymbol} {formatCurrency(amounts.tax_amount)}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 0.5 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1" fontWeight="bold">
                                    {isArabic ? "الإجمالي المستحق" : "Total Due"}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    {currencySymbol} {formatCurrency(amounts.total_due)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
                
                {invoiceData.payment_remark && (
                    <Grid item xs={12}>
                        <Box sx={{ mt: 1, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                                {isArabic ? "ملاحظات الدفع" : "Payment Remarks"}
                            </Typography>
                            <Typography variant="body2">
                                {invoiceData.payment_remark?.[lang] || invoiceData.payment_remark?.en || invoiceData.payment_remark?.ar || '-'}
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Paper>
    );
};

export default InvoiceOverview;
